
/**
 * Generates blood sugar data points based on fasting duration
 */
export const generateBloodSugarData = (
  hoursElapsed: number,
  totalDuration: number
): Array<{ hour: number; value: number }> => {
  const data = [];
  const totalHours = totalDuration / 3600;
  const hoursToShow = Math.max(Math.ceil(hoursElapsed) + 2, 6); // Show at least 6 hours or current + 2
  
  // Show data for past hours and project for next few hours
  for (let hour = 0; hour <= hoursToShow; hour++) {
    if (hour === 0) {
      // Starting blood sugar (normal level before a fast)
      data.push({ hour: 0, value: 95 });
    } else if (hour <= 2) {
      // Blood sugar rises after eating for about 2 hours
      const value = 95 + (hour * 20); // Rises to about 135
      const randomVariation = (Math.random() * 6) - 3; // -3 to +3
      data.push({ hour, value: Math.round(value + randomVariation) });
    } else if (hour <= 6) {
      // Blood sugar drops for 4 hours after the peak
      const peakValue = 135;
      const dropRate = (peakValue - 85) / 4; // Drop to normal range over 4 hours
      const value = peakValue - ((hour - 2) * dropRate);
      const randomVariation = (Math.random() * 6) - 3;
      data.push({ hour, value: Math.round(value + randomVariation) });
    } else if (hour <= 12) {
      // Gradual decrease after returning to normal
      const value = 85 - ((hour - 6) * 1.5); // Slow drop 
      const randomVariation = (Math.random() * 6) - 3;
      data.push({ hour, value: Math.round(value + randomVariation) });
    } else if (hour <= 24) {
      // Continued slower decrease
      const value = 85 - 9 - ((hour - 12) * 0.8);
      const randomVariation = (Math.random() * 4) - 2;
      data.push({ hour, value: Math.max(65, Math.round(value + randomVariation)) });
    } else {
      // Stabilizing at lower level
      const lastValue = data[data.length - 1].value;
      const decrease = Math.max(0.2, (lastValue - 65) / 24);
      const projectedValue = Math.max(65, lastValue - decrease);
      const randomVariation = (Math.random() * 2) - 1;
      data.push({ hour, value: Math.round(projectedValue + randomVariation) });
    }
  }
  
  return data;
};

/**
 * Gets the status text and color based on blood sugar value
 */
export const getBloodSugarStatus = (value: number): { 
  text: string; 
  color: string; 
} => {
  if (value > 125) return { text: "Higher than optimal", color: "text-amber-500" };
  if (value > 85) return { text: "Optimal range", color: "text-green-500" };
  if (value > 70) return { text: "Lower optimal range", color: "text-blue-500" };
  return { text: "Low - consider breaking fast", color: "text-red-500" };
};

/**
 * Calculates current blood sugar value based on elapsed time
 */
export const getCurrentBloodSugar = (
  bloodSugarData: Array<{ hour: number; value: number }>,
  hoursElapsed: number
): number => {
  if (bloodSugarData.length === 0) return 0;
  const currentIndex = Math.min(Math.floor(hoursElapsed), bloodSugarData.length - 1);
  return bloodSugarData[currentIndex].value;
};
